import React, {
  useState,
  useMemo,
  useCallback,
  memo,
  useEffect,
  useRef,
} from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  Tabs,
  Tab,
  Box,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { ArrowRightAlt, CameraOutdoorOutlined } from "@mui/icons-material";

import TemplatePreview from "../../widgets/TemplatePreview";
import { LanguageAutocomplete } from "../../widgets/LanguageAutocomplete";
import HeaderOptions from "./HeaderOptions";
import TemplateBody from "./TemplateBody";
import ButtonTemplateCreator from "./ButtonOptions";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import ConstructionIcon from "@mui/icons-material/Construction";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateAuthenticationTemplate from "./CreateAuthenticationTemplate";
import { useDialog } from "../../context/DialogContext";
import LoadingWidget from "../../widgets/LoadingWidget";
import WhatsAppCarouselCreator from "./WhatsAppCarouselCreator";
import { GlassButtonLoading } from "../../themes/UiElements";
export const CATEGORIES = [
  { type: "Marketing", icon: <CameraOutdoorOutlined /> },
  { type: "Utility", icon: <ConstructionIcon /> },
  { type: "Authentication", icon: <KeyOutlinedIcon /> },
  { type: "Media Carousel", icon: <CameraOutdoorOutlined /> },
];

const OptionalHeader = ({ label, subLabel }) => (
  <Typography variant="h6" gutterBottom>
    {label}
    <span style={{ fontWeight: "normal", fontSize: "small" }}>{subLabel}</span>
  </Typography>
);

function CreateSubmitTemplate() {
  const [searchParams] = useSearchParams();
  const tid = searchParams.get("tid");
  const p = searchParams.get("p");
  // Keep existing links: p=0 Marketing, p=1 Utility, p=2 Authentication,
  // p=3 Media Carousel. Standard templates share one visible tab.
  const pNumber = [0, 1, 2, 3].includes(Number(p)) ? Number(p) : 0;
  const { showDialog } = useDialog();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(pNumber < 2 ? 0 : pNumber);
  const [category, setCategory] = useState(
    pNumber === 1 ? "UTILITY" : "MARKETING"
  );
  const submitLock = useRef(false);
  const [lanCode, setLanCode] = useState("en_US");
  const [varType, setVarType] = useState("POSITIONAL");
  const [templateName, setTemplateName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [approveForAll, setApproveForAll] = useState(false);

  // header, body, footer, buttons
  const [header, setHeader] = useState({ type: "NONE" });
  const [bodyText, setBodyText] = useState("");
  const [bodyParams, setBodyParams] = useState({});
  const [footerText, setFooterText] = useState("");
  const [buttons, setButtons] = useState([]);
  /* ----------------------- derive components[] ----------------------- */
  const components = useMemo(() => {
    const comps = [];
    /* HEADER */
    if (header.type !== "NONE") {
      const comp = { type: "HEADER", format: header.type };
      switch (header.type) {
        case "TEXT":
          comp.text = header.text ?? "";
          if (header.param) {
            comp.example = {
              header_text: [header.param],
            };
          }
          break;
        default:
      }
      comps.push(comp);
    }
    /* BODY */
    if (bodyText.trim()) {
      const body = { type: "BODY", text: bodyText };
      const vars = Object.values(bodyParams).filter(Boolean);
      if (vars.length) {
        body.example = { body_text: [vars] };
      }
      comps.push(body);
    }
    if (footerText.trim()) comps.push({ type: "FOOTER", text: footerText });
    if (buttons.length) {
      const buttonBlocks = buttons.map((b) => {
        // Handle calls before the originalButton shortcut so expiry edits
        // also apply to templates loaded from the API.
        if (b.type === "whatsapp_call") {
          return {
            ...(b.originalButton || {}),
            type: "voice_call",
            text: (b.text || "").trim(),
            ttl_minutes: Number(
              b.ttlMinutes ?? b.originalButton?.ttl_minutes ?? 10080
            ),
          };
        }
        // Loaded buttons retain all provider fields until edited.
        if (b.originalButton) return b.originalButton;
        switch (b.type) {
          case "url":
            const urlButton = {
              type: "URL",
              text: b.text,
              url: b.url,
            };

            // Add example if dynamic URL with variable
            if (b.isDynamic && b.urlVariable?.example) {
              urlButton.url = `${b.url}?${b.urlVariable.name}={{1}}`;
              urlButton.example = `${b.url}?${b.urlVariable.name}=${b.urlVariable.example}`;
            }

            return urlButton;

          case "phone":
            return {
              type: "PHONE_NUMBER",
              text: b.text,
              phone_number: b.phone,
            };

          case "code":
            return {
              type: "COPY_CODE",
              code: b.code,
            };

          case "flow":
            return {
              type: "FLOW",
              text: b.text,
              flow_id: b.flowId,
            };
          case "catalog":
            return { type: "CATALOG", text: b.text };

          default: // quick replies
            return { type: "QUICK_REPLY", text: b.text };
        }
      });
      comps.push({ type: "BUTTONS", buttons: buttonBlocks });
    }
    return comps;
  }, [header, bodyText, bodyParams, footerText, buttons]);

  /* ----------------------- final payload (memo) ----------------------- */
  const payload = useMemo(
    () => ({
      name: templateName.trim(),
      category,
      language: lanCode,
      components,
    }),
    [templateName, category, lanCode, components]
  );

  useEffect(() => {
    setActiveTab(pNumber < 2 ? 0 : pNumber);
    if (pNumber < 2) setCategory(pNumber === 1 ? "UTILITY" : "MARKETING");
  }, [pNumber]);
  useEffect(() => {
    if (!tid) return;
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_ENDPOINT}/api/get-template/${tid}`,
          getHeader()
        );
        const tdata = response.data;
        setTemplateName(tdata.name);
        setLanCode(tdata.language);
        const header = tdata.components.find((item) => item.type === "HEADER");
        const body = tdata.components.find((item) => item.type === "BODY");
        const footer = tdata.components.find((item) => item.type === "FOOTER");
        setBodyText(body?.text || "");
        setVarType(tdata.parameter_format || "POSITIONAL");
        setHeader(
          header
            ? {
                type: header.format,
                text: header.text || "",
                param: header.example?.header_text?.[0] || "",
              }
            : { type: "NONE" }
        );
        const savedButtons = tdata.components.find(
          (item) => item.type === "BUTTONS"
        );
        setButtons(
          (savedButtons?.buttons || []).map((button, index) => {
            const meta = {
              URL: ["url", "Visit website"],
              PHONE_NUMBER: ["phone", "Call Phone"],
              VOICE_CALL: ["whatsapp_call", "Call on WhatsApp"],
              COPY_CODE: ["code", "Copy offer code"],
              FLOW: ["flow", "Complete flow"],
              CATALOG: ["catalog", "View catalog"],
              QUICK_REPLY: ["text", "Quick reply"],
            }[button.type?.toUpperCase()] || ["existing", button.type];
            return {
              id: `saved-${tid}-${index}`,
              group: meta[0] === "text" ? "quickReply" : "cta",
              type: meta[0],
              name: meta[1],
              text: button.text || (meta[0] === "code" ? "Copy Code" : meta[1]),
              url: button.url || "",
              phone: button.phone_number || "",
              flowId: button.flow_id || "",
              code: button.code || button.example?.[0] || "",
              ...(meta[0] === "whatsapp_call" && {
                ttlMinutes: button.ttl_minutes ?? 10080,
              }),
              isDynamic: false,
              urlVariable: null,
              originalButton: button,
            };
          })
        );
        const cat = tdata.category;
        if (cat === "AUTHENTICATION") {
          setActiveTab(2);
        } else if (tdata.components.some((item) => item.type === "CAROUSEL")) {
          setActiveTab(3);
        } else {
          setActiveTab(0);
          setCategory(cat === "UTILITY" ? "UTILITY" : "MARKETING");
        }
        if (footer) setFooterText(footer.text);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [tid]);

  /* ----------------------- submit handler ----------------------- */
  const handleSubmit = useCallback(async () => {
    if (submitLock.current) return;
    const callButtons = buttons.filter(
      (button) => button.type === "whatsapp_call"
    );
    if (
      callButtons.length > 1 ||
      callButtons.some((button) => !(button.text || "").trim())
    ) {
      toast.error("Use one Call on WhatsApp button with a non-empty label");
      return;
    }
    const hasInvalidCallExpiry = callButtons.some((button) => {
      const minutes = Number(
        button.ttlMinutes ?? button.originalButton?.ttl_minutes ?? 10080
      );
      return !Number.isInteger(minutes) || minutes < 1440 || minutes > 43200;
    });
    if (hasInvalidCallExpiry) {
      toast.error("Call button expiration must be between 1 and 30 days");
      return;
    }

    if (!payload.name || !bodyText.trim()) {
      toast.error("Template name and body text are required");
      return;
    }

    if (/{{\s*[^}]+\s*}}$/.test(bodyText.trim())) {
      toast.error("Body text should not end with a variable placeholder");
      return;
    }
    submitLock.current = true;
    if (!tid) {
      try {
        setSubmitting(true);
        const response = await axios.post(
          `${API_ENDPOINT}/api/create-template-for-approval`,
          { ...payload, approveForAll },
          getHeader()
        );
        showDialog("Successful", "Your template has been sent for approval.", {
          type: "success",
          onConfirm: () => {
            navigate(`/home/${response.data.id}/template-tracker`);
          },
        });
      } catch (error) {
        toastError(error);
      } finally {
        submitLock.current = false;
        setSubmitting(false);
      }
    } else {
      try {
        setSubmitting(true);
        const response = await axios.post(
          `${API_ENDPOINT}/api/update-template/${tid}`,
          payload,
          getHeader()
        );
        showDialog("Successful", "Your update has been sent for approval", {
          type: "success",
          onConfirm: () => {
            navigate(`/home/${response.data.id}/template-tracker`);
          },
        });
      } catch (error) {
        toastError(error);
      } finally {
        submitLock.current = false;
        setSubmitting(false);
      }
    }
  }, [payload, bodyText, buttons, approveForAll, tid, showDialog, navigate]);
  const handleButtonChange = useCallback((newV) => {
    setButtons(newV);
  }, []);
  if (loading) {
    return (
      <Box mt={4}>
        <LoadingWidget label="Loading Template to edit..." />
      </Box>
    );
  }

  /* ----------------------- render ----------------------- */
  return (
    <Container maxWidth="xl" sx={{ p: 3, bgcolor: "#e7effa" }}>
      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => {
          const nextP = v === 0 && category === "UTILITY" ? 1 : v;
          navigate(`/home/create-template?p=${nextP}`);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        <Tab
          value={0}
          label="Standard"
          icon={<CameraOutdoorOutlined />}
          iconPosition="start"
        />
        <Tab
          value={2}
          label="Authentication"
          icon={<KeyOutlinedIcon />}
          iconPosition="start"
        />
        <Tab
          value={3}
          label="Media Carousel"
          icon={<CameraOutdoorOutlined />}
          iconPosition="start"
        />
      </Tabs>
      {activeTab < 2 && (
        <Grid container spacing={3}>
          {/* Left column (form) */}
          <Grid item xs={12} md={7}>
            {/* name + language */}
            <Paper sx={{ p: 2 }} elevation={0}>
              <Typography variant="h6" gutterBottom>
                Template details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="template-category-label">
                      Category
                    </InputLabel>
                    <Select
                      labelId="template-category-label"
                      label="Category"
                      value={category}
                      onChange={(event) => {
                        const nextCategory = event.target.value;
                        setCategory(nextCategory);
                        const nextParams = new URLSearchParams(searchParams);
                        nextParams.set(
                          "p",
                          nextCategory === "UTILITY" ? "1" : "0"
                        );
                        navigate(
                          { search: `?${nextParams.toString()}` },
                          { replace: true }
                        );
                      }}
                    >
                      <MenuItem value="MARKETING">Marketing</MenuItem>
                      <MenuItem value="UTILITY">Utility</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Template Name"
                    value={templateName}
                    onChange={(e) => {
                      let t = e.target.value.slice(0, 512);
                      t = t.toLowerCase().replace(/[^a-z0-9_-]/g, "");
                      setTemplateName(t);
                    }}
                    helperText={`${templateName.length}/512`}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <LanguageAutocomplete value={lanCode} onChange={setLanCode} />
                </Grid>
              </Grid>
            </Paper>

            {/* content */}
            <Paper sx={{ p: 2, mt: 2 }} elevation={0}>
              <Typography variant="h6" gutterBottom>
                Content
              </Typography>
              <FormControl disabled size="small" fullWidth sx={{ mt: 2 }}>
                <InputLabel>Variable Type</InputLabel>
                <Select
                  value={varType}
                  label="Variable Type"
                  onChange={(e) => setVarType(e.target.value)}
                >
                  <MenuItem value="POSITIONAL">Number</MenuItem>
                  <MenuItem value="NAMED">Name</MenuItem>
                </Select>
              </FormControl>

              {/* header */}
              <Box mt={2}>
                <OptionalHeader label="Header" subLabel=" (Optional)" />
                <HeaderOptions
                  variableType={varType}
                  value={header}
                  onMediaUrl={(newV) => {
                    setPreviewUrl(newV);
                  }}
                  onChange={setHeader}
                />
              </Box>

              {/* body */}
              <Box mt={2}>
                <OptionalHeader label="Body" subLabel=" (Required)" />
                <TemplateBody
                  variableType={varType}
                  value={bodyText}
                  params={bodyParams}
                  onChange={(txt, p) => {
                    setBodyText(txt);
                    setBodyParams(p);
                  }}
                />
              </Box>

              {/* footer */}
              <Box mt={2}>
                <OptionalHeader label="Footer" subLabel=" (Optional)" />
                <TextField
                  size="small"
                  fullWidth
                  value={footerText}
                  onChange={(e) => setFooterText(e.target.value.slice(0, 60))}
                  placeholder="Footer text (max 60 chars)"
                />
              </Box>
            </Paper>

            {/* buttons */}
            <Paper sx={{ p: 2, mt: 2 }} elevation={0}>
              <OptionalHeader label="Buttons" subLabel=" (Optional)" />
              <ButtonTemplateCreator
                value={buttons}
                category={category}
                allowWhatsAppCall
                onChange={handleButtonChange}
              />
              <FormControlLabel
                sx={{ mt: 2 }}
                label="Approve this template for all my associated numbers"
                control={
                  <Checkbox
                    checked={approveForAll}
                    onChange={(e) => setApproveForAll(e.target.checked)}
                  />
                }
              />
            </Paper>
          </Grid>

          {/* Right column (preview + submit) */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, position: "sticky", top: 20 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Typography variant="h6">Preview</Typography>
              </Stack>
              <TemplatePreview template={payload} previewUrl={previewUrl} />
              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <GlassButtonLoading
                  variant="contained"
                  loading={submitting}
                  endIcon={<ArrowRightAlt />}
                  onClick={handleSubmit}
                >
                  Submit Template
                </GlassButtonLoading>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
      {activeTab === 2 && <CreateAuthenticationTemplate />}
      {activeTab === 3 && <WhatsAppCarouselCreator />}
    </Container>
  );
}

export default memo(CreateSubmitTemplate);
